import React, { useContext } from 'react';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostDnsPoliciesContext from '@totem/components/ubiquiti/hosts/details/dnsPolicies/ubiquitiHostDnsPoliciesContext';
import {
  UbiquitiApiDataItem,
  UbiquitiDnsPolicy,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

// Each DNS record type surfaces its "value" under a different field name —
// this picks the one relevant to the record's type for a single unified column.
const getRecordValue = (policy: UbiquitiDnsPolicy): string => {
  switch (policy.type) {
    case 'A_RECORD':
      return policy.ipv4Address ?? '';
    case 'AAAA_RECORD':
      return policy.ipv6Address ?? '';
    case 'CNAME_RECORD':
      return policy.targetDomain ?? '';
    case 'MX_RECORD':
      return `${policy.mailServerDomain ?? ''} (priority ${policy.priority})`;
    case 'SRV_RECORD':
      return `${policy.serverDomain ?? ''}:${policy.port} (priority ${policy.priority}, weight ${policy.weight})`;
    case 'TXT_RECORD':
      return policy.text ?? '';
    case 'FORWARD_DOMAIN':
      return policy.ipAddress ?? '';
    default:
      return '';
  }
};

const UbiquitiHostDnsPoliciesTable = () => {
  const { loading, error, dnsPolicies } = useContext(
    UbiquitiHostDnsPoliciesContext,
  );

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiDnsPolicy>>[] = [
    {
      title: 'Domain',
      dataIndex: ['data', 'domain'],
      key: 'domain',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.domain, compB.data.domain),
    },
    {
      title: 'Type',
      dataIndex: ['data', 'type'],
      key: 'type',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.type, compB.data.type),
      render: (_, record: UbiquitiApiDataItem<UbiquitiDnsPolicy>) => (
        <Tag>{record.data.type}</Tag>
      ),
    },
    {
      title: 'Value',
      key: 'value',
      render: (_, record: UbiquitiApiDataItem<UbiquitiDnsPolicy>) =>
        getRecordValue(record.data),
    },
    {
      title: 'TTL (seconds)',
      dataIndex: ['data', 'ttlSeconds'],
      key: 'ttlSeconds',
      render: (_, record: UbiquitiApiDataItem<UbiquitiDnsPolicy>) =>
        record.data.ttlSeconds ?? '',
    },
    {
      title: 'Enabled',
      dataIndex: ['data', 'enabled'],
      key: 'enabled',
      render: (_, record: UbiquitiApiDataItem<UbiquitiDnsPolicy>) =>
        record.data.enabled ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
  ];

  const exportHeaders = ['Domain', 'Type', 'Value', 'TTL (seconds)', 'Enabled'];
  const exportRows = dnsPolicies.map((policy) => [
    policy.data.domain,
    policy.data.type,
    getRecordValue(policy.data),
    policy.data.ttlSeconds ?? '',
    policy.data.enabled ? 'Yes' : 'No',
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="DNS policies unavailable"
        description="No DNS policy data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiDnsPolicy>>
      showSorterTooltip
      columns={columns}
      dataSource={dnsPolicies}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="dns-policies"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default UbiquitiHostDnsPoliciesTable;
